import React from 'react';
import logoGeorge from '../assets/logo_george.png';
import logoMaia from '../assets/logo_maia.svg';
import logoMaiaLegal from '../assets/logo_maia_legal.svg';
import logoLucia from '../assets/logo_lucia.svg';
import logoServicing from '../assets/logo_servicing.jpg';
import logoRetail from '../assets/logo_retail.jpg';
import logoAnalytics from '../assets/logo_analytics.jpg';
import logoProduct from '../assets/logo_product.jpg';
import logoHr from '../assets/logo_hr.jpg';
import logoJewel from '../assets/logo_jewel.png';

const Icons = {
  servicing: <img src={logoServicing} alt="Servicing" className="w-8 h-8" />,
  retail: <img src={logoRetail} alt="Retail" className="w-8 h-8" />,
  analytics: <img src={logoAnalytics} alt="Analytics" className="w-8 h-8" />,
  product: <img src={logoProduct} alt="Product" className="w-8 h-8" />,
  hr: <img src={logoHr} alt="HR" className="w-8 h-8" />,
  jewel: <img src={logoJewel} alt="Jewel" className="w-8 h-8" />,
  maia: <img src={logoMaia} alt="MaIA" className="w-8 h-8 object-contain" />,
  maiaLegal: <img src={logoMaiaLegal} alt="MaIA Legal" className="w-8 h-8 object-contain" />,
  lucia: <img src={logoLucia} alt="LucIA" className="w-8 h-8 object-contain" />,
  more: (
    <svg
      className="w-full h-full text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
      />
    </svg>
  ),
};

const AgentCard = ({
  icon,
  label,
  deactivated = false,
}: {
  icon: React.ReactNode;
  label: string;
  deactivated?: boolean;
}) => (
  <div
    className={`flex items-center gap-3 md:gap-4 bg-white border p-2 md:p-3 rounded-lg md:rounded-xl w-full md:w-44 shadow-sm transition-all z-20
    ${
      deactivated
        ? 'border-gray-100 opacity-60'
        : 'border-gray-100 hover:border-[#81D8D0] hover:shadow-md'
    }
  `}
  >
    <div
      className={`w-7 h-7 md:w-8 md:h-8 rounded-md flex items-center justify-center border p-0.5 flex-shrink-0
       ${deactivated ? 'bg-gray-50 border-gray-100' : 'bg-gray-50 border-gray-100'}
     `}
    >
      {icon}
    </div>
    <span
      className={`font-medium text-[11px] md:text-xs leading-tight
       ${deactivated ? 'text-gray-400' : 'text-gray-800'}
     `}
    >
      {label}
    </span>
  </div>
);

const GroupContainer = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-2 md:p-3 backdrop-blur-sm">
    <span className="text-[7px] md:text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 md:mb-2 block">
      {label}
    </span>
    <div className="flex flex-col gap-1.5 md:gap-2">{children}</div>
  </div>
);

interface ConnectionPathProps {
  d: string;
  id: string;
  color: string;
  gradientCoords?: { x1: string; y1: string; x2: string; y2: string };
}

const ConnectionPath: React.FC<ConnectionPathProps> = ({
  d,
  id,
  color,
  gradientCoords = { x1: '0%', y1: '0%', x2: '100%', y2: '0%' },
}) => (
  <>
    <path
      d={d}
      stroke="rgba(0,0,0,0.06)"
      strokeWidth="2"
      fill="none"
      vectorEffect="non-scaling-stroke"
    />
    <path
      d={d}
      stroke={`url(#${id})`}
      strokeWidth="2"
      fill="none"
      strokeDasharray="10 10"
      className="animate-flow"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
    <defs>
      <linearGradient id={id} gradientUnits="userSpaceOnUse" {...gradientCoords}>
        <stop offset="0%" stopColor={color} stopOpacity="0" />
        <stop offset="50%" stopColor={color} stopOpacity="1" />
        <stop offset="100%" stopColor="#81D8D0" stopOpacity="1" />
      </linearGradient>
    </defs>
  </>
);

const WorkflowSection: React.FC = () => {
  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-12 md:pb-20">
      <style>{`
        @keyframes flow {
          0% { stroke-dashoffset: 200; }
          100% { stroke-dashoffset: 0; }
        }
        .animate-flow {
          animation: flow 3s linear infinite;
        }
      `}</style>

      {/* Title */}
      <div className="text-center mb-8 md:mb-12">
        <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-black">
          A unified global assistant
        </h2>
        <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">
          George unifies your expert agents into one powerful conversational assistant.
        </p>
      </div>

      {/* Visualization Card */}
      <div className="relative bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden flex items-center justify-center">
        {/* Background Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:40px_40px]"></div>

        {/* Ambient Glows */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#81D8D0]/10 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/5 blur-[100px] rounded-full pointer-events-none"></div>

        <div className="relative w-full max-w-5xl px-2 md:px-16 py-8 md:py-12 flex items-center justify-center">
          {/* SVG connection lines */}
          <div className="absolute inset-0 w-full h-full pointer-events-none z-0 hidden md:block">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Tiffany → George */}
              <ConnectionPath id="grad1" color="#3B82F6" d="M 20,50 C 32,50 38,50 50,50" />
              {/* LVMH → George */}
              <ConnectionPath
                id="grad2"
                color="#14B8A6"
                d="M 80,50 C 68,50 62,50 50,50"
                gradientCoords={{ x1: '100%', y1: '0%', x2: '0%', y2: '0%' }}
              />
            </svg>
          </div>

          {/* DOM Layer */}
          <div className="relative w-full flex flex-col md:flex-row md:justify-between items-center z-10">
            {/* Top/Left column: Tiffany */}
            <div className="flex flex-col gap-3 md:gap-4 md:ml-12 w-full md:w-auto">
              <GroupContainer label="Tiffany">
                <div className="relative flex flex-col gap-1.5 md:gap-2">
                  {/* Vertical line — same style as ConnectionPath */}
                  <div className="absolute left-1/2 -translate-x-1/2 inset-y-0 w-1 pointer-events-none">
                    <svg className="w-full h-full" viewBox="0 0 2 100" preserveAspectRatio="none">
                      <path
                        d="M 1,0 L 1,100"
                        stroke="rgba(0,0,0,0.06)"
                        strokeWidth="2"
                        fill="none"
                        vectorEffect="non-scaling-stroke"
                      />
                      <path
                        d="M 1,0 L 1,100"
                        stroke="url(#vertGrad)"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray="10 10"
                        className="animate-flow"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                      />
                      <defs>
                        <linearGradient
                          id="vertGrad"
                          gradientUnits="userSpaceOnUse"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="100"
                        >
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
                          <stop offset="50%" stopColor="#3B82F6" stopOpacity="1" />
                          <stop offset="100%" stopColor="#81D8D0" stopOpacity="1" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className="relative z-10">
                    <AgentCard icon={Icons.servicing} label="Servicing Agent" />
                  </div>
                  <div className="relative z-10">
                    <AgentCard icon={Icons.retail} label="Retail Agent" />
                  </div>
                  <div className="relative z-10">
                    <AgentCard icon={Icons.analytics} label="Analytics Agent" />
                  </div>
                  <div className="relative z-10">
                    <AgentCard icon={Icons.product} label="Product Agent" />
                  </div>
                  <div className="relative z-10">
                    <AgentCard icon={Icons.hr} label="HR Agent" />
                  </div>
                  <div className="relative z-10 bg-white rounded-lg md:rounded-xl">
                    <AgentCard icon={Icons.more} label="More to come" deactivated />
                  </div>
                </div>
              </GroupContainer>
            </div>

            {/* Mobile connector: Tiffany → George */}
            <div className="md:hidden flex justify-center h-10 w-1">
              <svg className="w-full h-full" viewBox="0 0 2 100" preserveAspectRatio="none">
                <path
                  d="M 1,0 L 1,100"
                  stroke="rgba(0,0,0,0.06)"
                  strokeWidth="2"
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d="M 1,0 L 1,100"
                  stroke="url(#mobGrad1)"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="10 10"
                  className="animate-flow"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
                <defs>
                  <linearGradient
                    id="mobGrad1"
                    gradientUnits="userSpaceOnUse"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="100"
                  >
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="1" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Center: George */}
            <div className="flex flex-col justify-center items-center">
              <div className="w-20 h-20 md:w-48 md:h-48 bg-white rounded-xl md:rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col items-center justify-center relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-[#81D8D0]/20 to-transparent rounded-xl md:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <img
                  src={logoGeorge}
                  alt="George"
                  className="w-12 h-12 md:w-32 md:h-32 object-contain mb-1 md:mb-3 relative z-10"
                />
              </div>
              <div className="text-center mt-2 md:mt-6">
                <span className="text-gray-400 text-[8px] md:text-sm font-medium tracking-wide uppercase">
                  Global Assistant
                </span>
              </div>
            </div>

            {/* Mobile connector: George → LVMH */}
            <div className="md:hidden flex justify-center h-10 w-1">
              <svg className="w-full h-full" viewBox="0 0 2 100" preserveAspectRatio="none">
                <path
                  d="M 1,0 L 1,100"
                  stroke="rgba(0,0,0,0.06)"
                  strokeWidth="2"
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d="M 1,0 L 1,100"
                  stroke="url(#mobGrad2)"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="10 10"
                  className="animate-flow"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
                <defs>
                  <linearGradient
                    id="mobGrad2"
                    gradientUnits="userSpaceOnUse"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="100"
                  >
                    <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#14B8A6" stopOpacity="1" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Bottom/Right column: LVMH & Partners */}
            <div className="flex flex-col gap-3 md:gap-4 md:mr-12 w-full md:w-auto">
              <GroupContainer label="LVMH & Partners">
                <AgentCard icon={Icons.jewel} label="Jewel" />
                <AgentCard icon={Icons.maia} label="MaIA" />
                <AgentCard icon={Icons.maiaLegal} label="MaIA Legal" />
                <AgentCard icon={Icons.lucia} label="LucIA" />
              </GroupContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowSection;
